import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ExtractService } from '../../../shared/services/extract.service';
import { LoaderService } from '../../../shared/services/loader.service';
import { firstValueFrom } from 'rxjs';
import { HttpClientService } from '../../../shared/services/http-client.service';
import { PerfilParticipante } from '../../interface/perfil-participante';
import { AppStateService } from '../../../shared/services/app-state.service';
import { EnumStatus } from '../../enum/EnumStatus';
import { CampanhaPerfil } from '../../interface/campanha';
import { EnumStatusResponse } from '../../enum/EnumHttpResponse';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-token-verify',
  standalone: true,
  imports: [],
  templateUrl: './token-verify.component.html',
  styleUrls: ['./token-verify.component.scss'],
})
export class TokenVerifyComponent implements OnInit {
  perfilParticipante!: PerfilParticipante;
  campanha!: CampanhaPerfil;
  token?: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private extractService: ExtractService,
    private loaderService: LoaderService,
    private httpClientService: HttpClientService,
    private appStateService: AppStateService,
    private toastService: ToastService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.loaderService.showLoader();

    // Limpa mensagem de uma passagem anterior por /not-found.
    this.appStateService.clearMensagemNaoApto();

    // 1. Lê os parâmetros da URL
    const params: Params = await firstValueFrom(this.route.queryParams);

    // 2. Aceita tanto `token` quanto `Token`
    const tokenUrl = params['token'] || params['Token'];

    if (tokenUrl) {
      // 3. Token veio pela URL (prioridade): limpa o anterior e persiste o novo,
      //    garantindo que exista apenas um token válido no AppState.
      this.appStateService.clearState();
      this.appStateService.clearToken();
      this.appStateService.setToken(tokenUrl);
      this.token = tokenUrl;
    } else {
      // 4. Sem token na URL: recupera o token já salvo no AppState.
      this.token = this.appStateService.getToken();
    }

    // 5. Sem token válido (null/undefined/''): interrompe o fluxo.
    if (!this.token) {
      console.warn('[TokenVerify] Token ausente — redirecionando para /not-found.');
      this.loaderService.hideLoader();
      this.router.navigate(['/not-found']);
      return;
    }

    // Validação do token
    const decoded = this.extractService.getDecodedAccessToken(this.token);

    if (!decoded) {
      console.warn('[TokenVerify] Token inválido (não decodificado) — redirecionando para /not-found.');
      this.loaderService.hideLoader();
      this.router.navigate(['/not-found']);
      return;
    }

    // Token válido: segue o fluxo (participante → campanha → navegação por status).
    this.getPerfilParticipante(this.token);
  }

  private getPerfilParticipante(token: string): void {
    this.httpClientService.getPerfilParticipante(token)
      .then(response => {

        if (response.status === EnumStatusResponse.Success) {
          const body = response.body;
          let participante: PerfilParticipante | undefined;
          if (Array.isArray(body)) {
            participante = body[0] as PerfilParticipante;
          } else {
            participante = body as PerfilParticipante;
          }

          if (participante) {
            this.perfilParticipante = participante;
            this.appStateService.setPerfilParticipante(this.perfilParticipante);
          }

          if(this.perfilParticipante.elegivel === false) {
            this.setMensagemNaoApto(this.perfilParticipante?.detail);
            this.router.navigate(['/not-found']);
            this.loaderService.hideLoader();
            return;
          }

          if (this.perfilParticipante.idCampanha) {
            this.getCampaignAndNavigate(token, this.perfilParticipante.idCampanha);
          } else {
            this.navigateByStatus();
          }
        } else {
          // Status inesperado: evita o loader ficar aberto indefinidamente.
          this.setMensagemNaoApto((response?.body as PerfilParticipante)?.detail);
          this.router.navigate(['/not-found']);
          this.loaderService.hideLoader();
        }
      })
      .catch(error => {
        console.error('[TokenVerify] Participante não encontrado para os parâmetros fornecidos.', error);
        this.setMensagemNaoApto(error?.problem?.detail);
        this.router.navigate(['/not-found']);
        this.loaderService.hideLoader();
        return
      });
  }

  // Guarda o `detail` retornado pela API para que a tela /not-found o exiba.
  private setMensagemNaoApto(detail?: string): void {
    if (detail?.trim()) {
      this.appStateService.setMensagemNaoApto(detail.trim());
    }
  }

  private getCampaignAndNavigate(token: string, idCampanha: number): void {

  this.httpClientService.getCampanha(token, idCampanha)
    .then(response => {

      if (response.status === EnumStatusResponse.Success) {
        const body = response.body;

        const campanha = Array.isArray(body)
          ? body[0] as CampanhaPerfil
          : body as CampanhaPerfil;

        if (campanha) {
          this.campanha = campanha;
          this.appStateService.setCampanha(this.campanha);
        }
      }

      this.navigateByStatus();
    })
    .catch(error => {
      console.error('Erro ao buscar campanha:', error);
      this.navigateByStatus();
    });
}

  private navigateByStatus(): void {
    switch (this.perfilParticipante?.status) {
      case EnumStatus.Solicitado:
        if(this.perfilParticipante?.idPerfilSelecionado) {
          this.appStateService.setSolicitacaoAndamento(true);
          this.appStateService.setSelectedProfile(this.perfilParticipante?.idPerfilSelecionado);
          this.router.navigate(['/term']);
        } else {
          this.toastService.show('Não foi possivel identificar o perfil selecionado.', 'error');
          this.router.navigate(['/not-found']);
        }
        break;

      case EnumStatus.Iniciado:
        this.appStateService.setSolicitacaoAndamento(false);
        this.router.navigate(['/home']);
        break;

      case EnumStatus.Finalizado:
        this.appStateService.setSolicitacaoAndamento(false);
        this.router.navigate(['/term']);
        break;

      case EnumStatus.Cancelado:
        this.appStateService.setSolicitacaoAndamento(false);
        this.router.navigate(['/home']);
        break;
      case EnumStatus.Pendente:
        this.appStateService.setSolicitacaoAndamento(false);
        this.router.navigate(['/home']);
        break;
    }

    this.loaderService.hideLoader();
  }
}
